import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Edit, Tag, Percent, GraduationCap } from 'lucide-react';
import { formatPrice } from '../../lib/utils';

const DiscountsTab = ({
  discounts,
  classes = [],
  bootcamps = [],
  discountForm,
  setDiscountForm,
  discountModalOpen,
  setDiscountModalOpen,
  editingDiscount,
  setEditingDiscount,
  handleCreateDiscount,
  handleDeleteDiscount
}) => {
  const openNewDiscount = () => {
    setEditingDiscount(null);
    setDiscountForm({
      name: '', description: '', discount_type: 'percentage', value: 10,
      applies_to: 'all', code: '', is_active: true,
      max_uses: '', valid_from: '', valid_until: '',
      class_ids: [], bootcamp_ids: [], student_ids: []
    });
    setDiscountModalOpen(true);
  };

  const openEditDiscount = (discount) => {
    setEditingDiscount(discount);
    setDiscountForm({
      name: discount.name,
      description: discount.description || '',
      discount_type: discount.discount_type,
      value: discount.value,
      applies_to: discount.applies_to || 'all',
      code: discount.code || '',
      is_active: discount.is_active,
      max_uses: discount.max_uses || '',
      valid_from: discount.valid_from || '',
      valid_until: discount.valid_until || '',
      class_ids: discount.class_ids || [],
      bootcamp_ids: discount.bootcamp_ids || [],
      student_ids: discount.student_ids || []
    });
    setDiscountModalOpen(true);
  };

  const toggleId = (field, id) => {
    const current = discountForm[field] || [];
    const next = current.includes(id)
      ? current.filter(x => x !== id)
      : [...current, id];
    setDiscountForm({ ...discountForm, [field]: next });
  };

  const appliesToLabel = (a) => ({
    all: 'All Classes & Camps',
    specific_classes: 'Specific Classes',
    specific_students: 'Specific Students',
    bootcamps: 'All Camps',
    specific_bootcamps: 'Specific Camps'
  }[a] || a);

  return (
    <div data-testid="discounts-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-semibold text-white">Discounts & Scholarships</h2>
        <Button 
          onClick={openNewDiscount}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="add-discount-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Discount
        </Button>
      </div>

      <div className="grid gap-4">
        {discounts.map(discount => (
          <Card key={discount.id} className="bg-[#111] border-[#333]">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  discount.discount_type === 'percentage' ? 'bg-orange-500/20' : 'bg-emerald-500/20'
                }`}>
                  {discount.discount_type === 'percentage' ? (
                    <Percent className="w-6 h-6 text-orange-400" />
                  ) : (
                    <Tag className="w-6 h-6 text-emerald-400" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">{discount.name}</h3>
                    {discount.code && (
                      <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 font-mono">
                        {discount.code}
                      </Badge>
                    )}
                    {!discount.is_active && (
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-white/60">{discount.description || 'No description'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="bg-[#333] text-white/70">
                      {appliesToLabel(discount.applies_to || 'all')}
                    </Badge>
                    {discount.max_uses && (
                      <span className="text-xs text-white/50">
                        {discount.used_count || 0}/{discount.max_uses} uses
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className={`font-mono text-2xl font-bold ${
                    discount.discount_type === 'percentage' ? 'text-orange-400' : 'text-emerald-400'
                  }`}>
                    {discount.discount_type === 'percentage' 
                      ? `${discount.value}%` 
                      : formatPrice(discount.value)}
                  </p>
                  <p className="text-xs text-white/50">
                    {discount.discount_type === 'percentage' ? 'off' : 'discount'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => openEditDiscount(discount)}
                    className="text-white/50 hover:text-white"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDeleteDiscount(discount.id)}
                    className="text-red-400/50 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {discounts.length === 0 && (
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="py-12 text-center text-white/50">
              <GraduationCap className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p>No discounts yet. Create your first discount or scholarship!</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Discount Modal */}
      <Dialog open={discountModalOpen} onOpenChange={setDiscountModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#111] border-[#333]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingDiscount ? 'Edit Discount' : 'Create Discount'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Name *</Label>
                <Input 
                  value={discountForm.name} 
                  onChange={e => setDiscountForm({...discountForm, name: e.target.value})}
                  placeholder="Early Bird"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Code (optional)</Label>
                <Input 
                  value={discountForm.code} 
                  onChange={e => setDiscountForm({...discountForm, code: e.target.value.toUpperCase()})}
                  placeholder="EARLYBIRD"
                  className="bg-[#0a0a0a] border-[#333] text-white font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Description</Label>
              <Textarea 
                value={discountForm.description} 
                onChange={e => setDiscountForm({...discountForm, description: e.target.value})}
                placeholder="Optional description..."
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Type</Label>
                <Select 
                  value={discountForm.discount_type} 
                  onValueChange={v => setDiscountForm({...discountForm, discount_type: v})}
                >
                  <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Value</Label>
                <Input 
                  type="number" 
                  step={discountForm.discount_type === 'percentage' ? '1' : '0.01'}
                  value={discountForm.value} 
                  onChange={e => setDiscountForm({...discountForm, value: parseFloat(e.target.value)})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Applies To</Label>
              <Select 
                value={discountForm.applies_to} 
                onValueChange={v => setDiscountForm({...discountForm, applies_to: v})}
              >
                <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white" data-testid="discount-applies-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes & Camps</SelectItem>
                  <SelectItem value="specific_classes">Specific Classes</SelectItem>
                  <SelectItem value="bootcamps">All Camps</SelectItem>
                  <SelectItem value="specific_bootcamps">Specific Camps</SelectItem>
                  <SelectItem value="specific_students">Specific Students</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Specific Classes picker */}
            {discountForm.applies_to === 'specific_classes' && (
              <div className="space-y-2" data-testid="discount-class-picker">
                <Label className="text-white/70">
                  Pick classes ({(discountForm.class_ids || []).length} selected)
                </Label>
                <div className="max-h-52 overflow-y-auto border border-[#333] rounded-md bg-[#0a0a0a] p-2 space-y-1">
                  {classes.length === 0 && (
                    <p className="text-xs text-white/40 p-2">No classes loaded.</p>
                  )}
                  {classes.map(cls => (
                    <label
                      key={cls.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={(discountForm.class_ids || []).includes(cls.id)}
                        onChange={() => toggleId('class_ids', cls.id)}
                        className="w-4 h-4 accent-orange-500"
                        data-testid={`discount-class-${cls.id}`}
                      />
                      <span className="text-sm text-white flex-1 truncate">{cls.name}</span>
                      {cls.category && (
                        <span className="text-[10px] text-white/40 uppercase">{cls.category}</span>
                      )}
                    </label>
                  ))}
                </div>
                {(discountForm.class_ids || []).length === 0 && (
                  <p className="text-xs text-amber-400">⚠ Pick at least one class or this code won't apply anywhere.</p>
                )}
              </div>
            )}

            {/* Specific Camps picker */}
            {discountForm.applies_to === 'specific_bootcamps' && (
              <div className="space-y-2" data-testid="discount-bootcamp-picker">
                <Label className="text-white/70">
                  Pick camps ({(discountForm.bootcamp_ids || []).length} selected)
                </Label>
                <div className="max-h-52 overflow-y-auto border border-[#333] rounded-md bg-[#0a0a0a] p-2 space-y-1">
                  {bootcamps.length === 0 && (
                    <p className="text-xs text-white/40 p-2">No camps found.</p>
                  )}
                  {bootcamps.map(bc => (
                    <label
                      key={bc.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={(discountForm.bootcamp_ids || []).includes(bc.id)}
                        onChange={() => toggleId('bootcamp_ids', bc.id)}
                        className="w-4 h-4 accent-orange-500"
                        data-testid={`discount-bootcamp-${bc.id}`}
                      />
                      <span className="text-sm text-white flex-1 truncate">{bc.name}</span>
                      {bc.start_date && (
                        <span className="text-[10px] text-white/40">{bc.start_date}</span>
                      )}
                    </label>
                  ))}
                </div>
                {(discountForm.bootcamp_ids || []).length === 0 && (
                  <p className="text-xs text-amber-400">⚠ Pick at least one camp or this code won't apply anywhere.</p>
                )}
              </div>
            )}

            {/* Specific Students note */}
            {discountForm.applies_to === 'specific_students' && (
              <div className="text-xs text-white/60 bg-[#0a0a0a] border border-[#333] rounded-md p-3">
                Student-specific discounts are managed from <strong>Admin → Students → student profile → Discounts</strong>. Saving here without IDs will make the code valid for no one.
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-white/70">Max Uses</Label>
              <Input 
                type="number" 
                value={discountForm.max_uses} 
                onChange={e => setDiscountForm({...discountForm, max_uses: e.target.value})}
                className="bg-[#0a0a0a] border-[#333] text-white"
                placeholder="Unlimited"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Valid From</Label>
                <Input 
                  type="date" 
                  value={discountForm.valid_from} 
                  onChange={e => setDiscountForm({...discountForm, valid_from: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Valid Until</Label>
                <Input 
                  type="date" 
                  value={discountForm.valid_until} 
                  onChange={e => setDiscountForm({...discountForm, valid_until: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={discountForm.is_active}
                onChange={e => setDiscountForm({...discountForm, is_active: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <Label className="text-white/70">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscountModalOpen(false)} className="border-[#333] text-white/70">
              Cancel
            </Button>
            <Button onClick={handleCreateDiscount} className="bg-orange-500 hover:bg-orange-600" data-testid="save-discount-btn">
              {editingDiscount ? 'Update' : 'Create'} Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountsTab;
